import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';
import signatureImage from '../assets/images/dclogo.png';

const GDSRList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'dsr_no', 'subject', 'status'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await axios.post('https://chaman.gob.pk/eac_backend/adddsr.php', {
                action: 'get_records',
            });

            if (Array.isArray(response.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = response.data
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Expected array but got:', response.data);
            }
        } catch (err) {
            setError('Failed to fetch General DSR entries');
            console.error(err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);


    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/adddsr.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-general-dsr', { state: { entry } });
    };

    const handlePrint = async (entry) => {
        const doc = new jsPDF('p', 'mm', 'legal'); // 'p' for portrait, 'mm' for millimeters, 'legal' for page size
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Format date to DD-MM-YYYY
        const formattedDate = new Date(entry.date).toLocaleDateString('en-GB'); // Converts to DD-MM-YYYY

        // Add logo and header
        const imageWidth = 0.8 * 20;
        const imageHeight = 0.7 * 20;
        const xCenter = (pageWidth - imageWidth) / 2;
        doc.addImage(logoImage, 'PNG', xCenter, 10, imageWidth, imageHeight);
        doc.setFontSize(22);
        doc.setFont('times', 'Bold');
        doc.text('OFFICE OF THE DEPUTY COMMISSIONER CHAMAN', 14, 37);

        // Add DSR No. and Date
        doc.setFontSize(10);
        doc.text(`DSR No.: ${entry.dsr_no}`, 14, 42);
        doc.text(`Date: ${formattedDate}`, pageWidth - 40, 42); // Updated with formatted date

        // Add "To" section
        doc.setFontSize(10);
        doc.text('To:', 14, 50);
        doc.text('The Director General Balochistan Levies Force', 50, 55);
        doc.text('Quetta', 50, 60);

        // Add Subject Label
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text('SUBJECT:', 14, 70);

        const subjectX = 50;
        const maxSubjectWidth = pageWidth - 70;
        const subjectText = (entry.subject || '').toUpperCase();
        const subjectLines = doc.splitTextToSize(subjectText, maxSubjectWidth);

        // Apply bold style to subject text
        doc.setFont('helvetica', 'bold').setFontSize(11);
        doc.text(subjectLines, subjectX, 70);

        // Underline subject
        subjectLines.forEach((line, index) => {
            const lineWidth = doc.getTextWidth(line);
            const lineY = 70 + (index * 7);
            doc.setLineWidth(0.5);
            doc.line(subjectX, lineY + 1, subjectX + lineWidth, lineY + 1);
        });

        // Reset font for statement
        doc.setFont('helvetica', 'normal').setFontSize(10);

        // Add statement
        const statementY = 70 + subjectLines.length * 7;
        const statementLines = doc.splitTextToSize(entry.statement || '', maxSubjectWidth);
        doc.text(statementLines, 50, statementY);

        // Add table
        const tableY = statementY + statementLines.length * 4.5 + 0.5;
        doc.autoTable({
            head: [["S.No.", "Event", "Description"]],
            body: [
                ["1", "Sabotage/Terrorism", entry.sabotage],
                ["2", "Crime and its nature", entry.crime],
                ["3", "Political/National Activities/Procession/Rallies", entry.politics],
                ["4", "Religious/Sectarian Crimes", entry.religious],
                ["5", "General Strikes/Rallies", entry.strikes],
                ["6", "Highway Incidents", entry.incidents],
                ["7", "Recovery arms/Ammo, Vehicle", entry.recovery],
                ["8", "Miscellaneous/General Event", entry.miscellaneous],
                ["9", "Near Future Events in the District", entry.events],
                ["10", "Arrival / Departure of VIPs/ VVIPs", entry.arrival],
                ["11", "Intelligence based operations", entry.intelligence],
                ["12", "General Raids", entry.raids],
                ["13", "Operation Against POS", entry.operation],
                ["14", "Narcotics / Liquor Seizure Reports", entry.narcotics],
                ["15", "Report of Torrential Rains", entry.rains],
            ],
            startY: tableY,
            theme: 'grid',
            headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
            styles: { fontSize: 9, textColor: [0, 0, 0] },
            columnStyles: { 0: { halign: 'center', cellWidth: 15 } },
        });

        // Footer Section
        const footerY = Math.max(doc.autoTable.previous.finalY + 5, pageHeight - 60);
        const deputyTextX = 148;
        const deputyTextY = footerY;

        // Signature Image
        const signatureWidth = 0.8 * 25;
        const signatureHeight = 0.6 * 25;
        const signatureX = 163;
        const signatureY = deputyTextY - signatureHeight - 1;
        doc.addImage(signatureImage, 'PNG', signatureX, signatureY, signatureWidth, signatureHeight);

        // Add "DEPUTY COMMISSIONER" text
        doc.setFontSize(10).setFont('helvetica', 'normal');
        doc.text('DEPUTY COMMISSIONER', deputyTextX, deputyTextY);
        doc.text('CHAMAN', 163, footerY + 5);

        // "Copy To" Section
        const copyToY = footerY + 10;
        doc.text('Copy To:', 14, copyToY);

        const copyToNames = [
            '1. The Additional Chief Secretary, Home & T.A Dept; Balochistan',
            '2. The Commissioner Quetta Division, Quetta',
            '3. The Additional Secretary (Staff) to the Chief Secretary, Balochistan',
        ];

        const copyToLines = doc.splitTextToSize(copyToNames.join('\n'), pageWidth - 25);
        doc.text(copyToLines, 25, copyToY + 5);

        // QR Code Generation
        const qrData = `General DSR ${entry.dsr_no} issued by the Office of the Deputy Commissioner, Chaman.`;
        const qrCanvas = document.createElement('canvas');

        await QRCode.toCanvas(qrCanvas, qrData, { width: 100 });
        const qrDataURL = qrCanvas.toDataURL('image/png');

        const qrX = 100;
        const qrY = pageHeight - 30;
        doc.addImage(qrDataURL, 'PNG', qrX, qrY, 20, 20);

        // Open the generated PDF
        window.open(doc.output('bloburl'));
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="gdtable-row">
                <div className="table-cell">{entry.token_id}</div>
                <div className="table-cell">{entry.date}</div>
                <div className="table-cell">{entry.dsr_no}</div>
                <div className="table-cell">{entry.subject}</div>
                {/* <div className="table-cell">{entry.cnic}</div> */}
                <div className="table-cell actions btn-group">
                    <button onClick={() => handlePrint(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>

            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading ...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    {/* <h2>Local Certificate Entries</h2> */}
                    <span className="entries-count">Total Records {gdsrEntries.length}</span>
                </div>

                <div className="header-right">
                    <div className="search-refresh-wrapper">
                        <button
                            onClick={refreshData}
                            className="refresh-button"
                            disabled={isRefreshing}
                            aria-label="Refresh data"
                        >
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={isRefreshing}
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                            />
                        </button>

                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search general DSR..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>




            <div className="table-container">
                <div className="gdtable-header">
                    <div className="header-cell">Token ID</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">DSR No.</div>
                    <div className="header-cell">Subject</div>
                    {/* <div className="header-cell">CNIC</div> */}
                    <div className="header-cell">Actions</div>
                </div>

                <div className="table-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={60}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            <p>No entries found {searchTerm ? `matching "${searchTerm}"` : 'available'}</p>
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    Showing {filteredEntries.length} of {gdsrEntries.length} entries
                </div>
            </div>
        </div>
    );
};

export default GDSRList;